/**
 * URL Generation Utilities for QR Codes
 *
 * This module provides pure functions for generating QR code URLs
 * for sites and bays. All URLs are properly encoded to handle
 * special characters in IDs.
 *
 * Requirements: 2.4, 2.5
 */

/**
 * Base URL for QR codes
 * This is the target URL that QR codes will encode
 */
export const QR_BASE_URL = 'https://app.pocket.toptracer.com' as const;

/**
 * Generates the QR code URL for a site with key parameter.
 *
 * @param siteId - The unique identifier for the site
 * @param siteKey - The site key for URL validation
 * @returns The encoded URL for the site QR code
 *
 * Requirement 2.4: WHEN "Site QR" is selected, THE QR_Generator SHALL encode
 * the URL `https://app.pocket.toptracer.com?siteId={siteId}`
 * Requirement 3.1: WHEN generating a QR code URL, THE URL_Generator SHALL append
 * the `k` query parameter with a valid site key
 * Requirement 3.4: THE URL_Generator SHALL properly encode the key value in the URL
 */
export function generateSiteQrUrl(siteId: string, siteKey: string): string {
  return `${QR_BASE_URL}?siteId=${encodeURIComponent(siteId)}&k=${encodeURIComponent(siteKey)}`;
}

/**
 * Generates the QR code URL for a specific bay with key parameter.
 *
 * @param siteId - The unique identifier for the site
 * @param bayId - The unique identifier for the bay
 * @param siteKey - The site key for URL validation
 * @returns The encoded URL for the bay QR code
 *
 * Requirement 2.5: WHEN a specific bay is selected, THE QR_Generator SHALL encode
 * the URL `https://app.pocket.toptracer.com?siteId={siteId}&bayId={bayId}`
 * Requirement 3.1: WHEN generating a QR code URL, THE URL_Generator SHALL append
 * the `k` query parameter with a valid site key
 * Requirement 3.4: THE URL_Generator SHALL properly encode the key value in the URL
 */
export function generateBayQrUrl(siteId: string, bayId: string, siteKey: string): string {
  return `${QR_BASE_URL}?siteId=${encodeURIComponent(siteId)}&bayId=${encodeURIComponent(bayId)}&k=${encodeURIComponent(siteKey)}`;
}
